"use client";

import { SelectedSkillBadges } from "@/components/selected-skill-badges";
import { useAgentSkills } from "@/hooks/use-agent-skills";
import { prependSelectedSkills } from "@/lib/selected-skills";

/**
 * ChildSessionPanel — responsive right-sidebar panel that displays a
 * child session (manual thread or sub-agent transcript) alongside the parent
 * conversation. Sub-agent sessions are detected from their persisted data and
 * rendered read-only through this same shell.
 *
 * Desktop: slides in as a resizable right sidebar (same pattern as
 * MarkdownViewerPanel / MarkdownViewerSidebar).
 * Mobile: full-screen Dialog overlay.
 *
 * The panel and the Markdown viewer are mutually exclusive — the parent
 * (ChatPage) ensures only one is open at a time via a discriminated union.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  Bot,
  ArrowLeft,
  Maximize2,
  MessageSquare,
  RotateCcw,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ChatStatus, FileUIPart } from "ai";
import { Button } from "@/components/ui/button";
import { InputGroupButton } from "@/components/ui/input-group";
import { SquareIcon } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { ChildSessionPanelShell } from "@/components/child-session-panel-shell";
import { AgentDropdown } from "@/components/agent-dropdown";
import { AgentInfoTag } from "@/components/agent-badge";
import { SessionBreadcrumb } from "@/components/session-breadcrumb";
import { ConversationMessageList } from "@/components/conversation-message-list";
import { SubAgentStateBadge } from "@/components/sub-agent-state-badge";
import { MarkdownViewer } from "@/components/markdown-viewer";
import { InterruptedRunBanner } from "@/components/interrupted-run-banner";
import {
  PromptInputProvider,
  useOptionalPromptInputController,
} from "@/components/ai-elements/prompt-input";
import { AttachFilesButton } from "@/components/ai-elements/attach-files-button";
import { type VoiceNoteButtonHandle } from "@/components/ai-elements/voice-note-button";
import { ChatPromptComposer, CHAT_MAX_UPLOAD_FILES, CHAT_MAX_UPLOAD_SIZE_MB, CHAT_MAX_UPLOAD_SIZE_BYTES } from "@/components/chat-prompt-composer";
import { PromptQueueList } from "@/components/prompt-queue-list";
import { ComposerQuickActions } from "@/components/composer-quick-actions";
import { FollowUpSuggestions, lastCompletedAssistantMessageId } from "@/components/follow-up-suggestions";
import { EnterToSendHint, OPEN_PREFERENCES_EVENT, useEnterToSendHint } from "@/components/enter-to-send-hint";
import { useIsMobile } from "@/hooks/use-media-query";
import { usePromptQueue } from "@/hooks/use-prompt-queue";
import { dispatchVoiceDictationShortcut } from "@/hooks/use-voice-dictation-shortcut";
import type { AgnoChatMessage } from "@/hooks/use-agno-chat";
import { useBackendReconnectKey } from "@/hooks/use-backend-connectivity";
import type { Agent } from "@/hooks/use-agents";
import { useSessionMessageFeed } from "@/hooks/use-session-message-feed";
import { useConversationReadEngagement } from "@/hooks/use-conversation-read-engagement";
import { useSessionBreadcrumb } from "@/hooks/use-session-breadcrumb";
import {
  SUB_AGENT_ACTIVE_POLL_INTERVAL_MS,
  subAgentMessageStateFromRows,
  useSubAgentSessionState,
  type SubAgentMessageObservation,
  type SubAgentSessionTarget,
} from "@/hooks/use-sub-agent-session-state";
import type { AsidePanelResize } from "@/hooks/use-aside-view";
import {
  fetchWithAuth as fetchWithWorkOSAuth,
  type AccessTokenGetter,
} from "@/lib/utils";
import {
  beginDraftAttempt,
  completeDraftAttempt,
  DRAFTS_CHANGED_EVENT,
  getDraft,
  getDraftAttempt,
  getDraftAttemptFiles,
  setDraft,
  type DraftAttempt,
} from "@/lib/drafts";
import { threadAnchorDisplayFrom } from "@/lib/session";
import { isOlderLastActiveAt } from "@/lib/thread-session-reconciliation";
import type {
  Session,
  ThreadAnchorDisplay,
  ThreadAnchorRef,
} from "@/lib/session";
import type { ConversationDestination } from "@/lib/conversation-target";
import {
  isSubAgentSessionData,
  isTerminalSubAgentState,
  type SubAgentThreadReference,
} from "@/lib/sub-agent-thread";

export interface ChildSessionPanelProps {
  /** Force a regular session transcript read-only, including before detail loads. */
  readOnly?: boolean;
  /** The child (thread) session ID. */
  childSessionId: string;
  /** The parent session ID. */
  parentSessionId: string;
  /** Access-token getter used by the authenticated chat APIs. */
  getAccessToken: AccessTokenGetter;
  /** The child session's agent ID (falls back to its parent while loading). */
  agentId: string | null;
  /** Agents available to the current user for this thread. */
  allAgents: Agent[];
  /** Authenticated user metadata forwarded with chat requests. */
  user: any;
  /** Immutable thread origin fields, cached from creation or anchor queries. */
  threadAnchor?: ThreadAnchorDisplay | null;
  /** Current thread title, resolved from its anchor reference or session. */
  threadTitle?: string | null;
  /** Parent title, used to keep the thread-panel header consistent with the main breadcrumb. */
  parentSessionTitle?: string | null;
  /** Close the panel. */
  onClose: () => void;
  onBack?: () => void;
  /** Clears a deep link whose child target no longer exists or is mismatched. */
  onInvalidTarget?: () => void;
  /** Open the child session as a full-screen view. */
  onOpenFullSession: (childSessionId: string) => void;
  /** Replace this panel with another non-root session from its breadcrumb. */
  onNavigateSession?: (destination: ConversationDestination) => void;
  /** Notify the parent after the child agent change has been persisted. */
  onAgentChanged?: (childSessionId: string, agentId: string | null) => void;
  /** Notify the parent when the child run reaches a terminal state. */
  onRunComplete?: (
    childSessionId: string,
  ) => Session | null | void | Promise<Session | null | void>;
  /** Opens a Markdown file linked from a thread message in the parent viewer. */
  onFileLinkClick?: (url: string, fileName: string) => void;
  /** A spawn inside a manual thread replaces this aside with a read-only child. */
  onOpenSubAgent?: (reference: SubAgentThreadReference) => void;
  onOpenWorkflowRun?: (taskId: string) => void;
  onSubAgentReferencesResolved?: (
    references: readonly SubAgentThreadReference[],
  ) => void;
  /** Optional lifecycle seed from the card; never used to decide read-only mode. */
  initialSubAgentReference?: SubAgentThreadReference | null;
  /** Effective server-backed desktop send shortcut. */
  submitShortcut?: "enter" | "ctrl_enter";
  /** Shared desktop resize controller from the active aside manager. */
  resize: AsidePanelResize;
}

export function ChildSessionPanel({
  readOnly = false,
  childSessionId,
  parentSessionId,
  getAccessToken,
  agentId,
  allAgents,
  user,
  threadAnchor,
  threadTitle,
  parentSessionTitle,
  onClose,
  onBack,
  onInvalidTarget,
  onOpenFullSession,
  onNavigateSession,
  onAgentChanged,
  onRunComplete,
  onFileLinkClick,
  onOpenSubAgent,
  onOpenWorkflowRun,
  onSubAgentReferencesResolved,
  initialSubAgentReference,
  submitShortcut = "enter",
  resize,
}: ChildSessionPanelProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(agentId);
  const [hasMessages, setHasMessages] = useState(false);
  const [messageObservation, setMessageObservation] =
    useState<SubAgentMessageObservation>({ count: 0, state: null });
  const [loadedSession, setLoadedSession] = useState<Session | null>(null);
  const currentChildSessionIdRef = useRef(childSessionId);
  currentChildSessionIdRef.current = childSessionId;
  const agentBindingRevisionRef = useRef(0);
  const locallyCommittedAgentRef = useRef<{
    childSessionId: string;
    agentId: string | null;
    revision: number;
  } | null>(null);
  const [sessionAccessUnavailable, setSessionAccessUnavailable] = useState(false);
  const [loadedThreadAnchor, setLoadedThreadAnchor] =
    useState<ThreadAnchorDisplay | null>(null);
  const invalidTargetNotifiedRef = useRef<string | null>(null);
  const backendReconnectKey = useBackendReconnectKey();

  const email = user?.email;
  const firstName = user?.firstName;
  const lastName = user?.lastName;
  const fetchWithAuth = useMemo(
    () => async (url: string, init: RequestInit): Promise<Response> => {
      const headers = new Headers(init.headers);
      headers.set("X-User-Email", email ?? "");
      headers.set(
        "X-User-Name",
        firstName ? (firstName + " " + (lastName ?? "")).trim() : "",
      );
      return fetchWithWorkOSAuth(url, { ...init, headers }, getAccessToken);
    },
    [email, firstName, getAccessToken, lastName],
  );
  const notifyInvalidTarget = useCallback(() => {
    const targetKey = `${parentSessionId}:${childSessionId}`;
    if (invalidTargetNotifiedRef.current === targetKey) return;
    invalidTargetNotifiedRef.current = targetKey;
    onInvalidTarget?.();
  }, [childSessionId, onInvalidTarget, parentSessionId]);

  // Fetch the session for both provenance and caller capabilities. The anchor
  // query may already have warmed the parent cache, but it intentionally does
  // not carry management rights.
  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    const detailRequestAgentRevision = agentBindingRevisionRef.current;
    setLoadedSession(null);
    setSessionAccessUnavailable(false);
    setLoadedThreadAnchor(null);

    fetchWithAuth(
      `/api/sessions/${encodeURIComponent(childSessionId)}`,
      { headers: {}, signal: controller.signal },
    )
      .then((response) => {
        if (!response.ok) {
          if (response.status === 404) notifyInvalidTarget();
          throw new Error(`HTTP ${response.status}`);
        }
        return response.json() as Promise<Session>;
      })
      .then((session) => {
        if (cancelled) return;
        const isSubAgent = isSubAgentSessionData(session);
        const isSupportedThread = session.session_type === "thread"
          || session.session_type === null
          || session.session_type === undefined;
        if (
          session.id !== childSessionId
          || session.can_read === false
          || (!readOnly && !isSubAgent && (
            !isSupportedThread
            || session.parent_session_id !== parentSessionId
          ))
        ) {
          setSessionAccessUnavailable(true);
          notifyInvalidTarget();
          return;
        }
        const locallyCommittedAgent = locallyCommittedAgentRef.current;
        // A detail refresh may have started before an agent PATCH and resolve
        // afterward. Preserve that confirmed binding, while detail requests
        // started after it can still apply a genuine external change.
        if (
          locallyCommittedAgent
          && locallyCommittedAgent.childSessionId === childSessionId
          && locallyCommittedAgent.revision > detailRequestAgentRevision
        ) {
          setLoadedSession({
            ...session,
            agent_id: locallyCommittedAgent.agentId,
          });
        } else {
          setLoadedSession(session);
        }
        if (!readOnly && !isSubAgent && !threadAnchor) {
          setLoadedThreadAnchor(threadAnchorDisplayFrom(session.metadata?.thread_anchor));
        }
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setSessionAccessUnavailable(true);
        console.error(
          "[child-session-panel] Failed to load thread provenance for session",
          childSessionId,
          error,
        );
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [backendReconnectKey, childSessionId, fetchWithAuth, notifyInvalidTarget, parentSessionId, readOnly, threadAnchor]);

  const isSubAgentSession = isSubAgentSessionData(loadedSession);
  const isReadOnly = readOnly || isSubAgentSession;
  const canParticipateSession = Boolean(
    loadedSession
    && !isReadOnly
    && !sessionAccessUnavailable
    && (loadedSession.can_participate ?? true),
  );
  const canManageSession = !isReadOnly
    && (loadedSession?.can_manage ?? false);
  const resolvedBreadcrumb = useSessionBreadcrumb(
    loadedSession && !readOnly ? childSessionId : null,
    getAccessToken,
    loadedSession,
  );
  const subAgentTarget = useMemo<SubAgentSessionTarget>(() => {
    const seed = initialSubAgentReference?.childSessionId === childSessionId
      ? initialSubAgentReference
      : null;
    return {
      childSessionId,
      parentSessionId: loadedSession?.parent_session_id ?? seed?.parentSessionId ?? parentSessionId,
      state: seed?.state ?? "queued",
      viewAvailable: seed?.viewAvailable ?? true,
    };
  }, [childSessionId, initialSubAgentReference, loadedSession?.parent_session_id, parentSessionId]);
  const subAgentSnapshot = useSubAgentSessionState(subAgentTarget, fetchWithAuth, {
    enabled: isSubAgentSession,
    messageCount: messageObservation.count,
    messageState: messageObservation.state,
  });

  // Reset when opening a different child or when its persisted agent loads,
  // so a previous thread-specific choice never leaks over.
  useEffect(() => {
    setSelectedAgentId(loadedSession?.agent_id ?? agentId);
  }, [agentId, childSessionId, loadedSession?.agent_id]);

  useEffect(() => {
    setHasMessages(false);
    setMessageObservation({ count: 0, state: null });
  }, [childSessionId]);

  const handleMessageObservationChange = useCallback(
    (next: SubAgentMessageObservation) => {
      setMessageObservation((current) => (
        current.count === next.count && current.state === next.state
          ? current
          : next
      ));
    },
    [],
  );

  const onAgentChange = useCallback(async (nextAgentId: string) => {
    const previousAgentId = selectedAgentId;
    if (!canManageSession || nextAgentId === previousAgentId) return;

    setSelectedAgentId(nextAgentId);
    try {
      const response = await fetchWithAuth(
        `/api/sessions/${childSessionId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ agent_id: nextAgentId }),
        },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const updated = await response.json() as { agent_id?: string | null };
      const persistedAgentId = updated.agent_id ?? nextAgentId;
      const revision = agentBindingRevisionRef.current + 1;
      agentBindingRevisionRef.current = revision;
      locallyCommittedAgentRef.current = {
        childSessionId,
        agentId: persistedAgentId,
        revision,
      };
      setLoadedSession((current) => (
        current?.id === childSessionId
          ? { ...current, agent_id: persistedAgentId }
          : current
      ));
      setSelectedAgentId(persistedAgentId);
      onAgentChanged?.(childSessionId, persistedAgentId);
    } catch (error) {
      console.error(
        "[child-session-panel] Failed to update agent for session",
        childSessionId,
        error,
      );
      setSelectedAgentId(previousAgentId);
    }
  }, [canManageSession, childSessionId, fetchWithAuth, onAgentChanged, selectedAgentId]);

  const displayAnchor = threadAnchor ?? loadedThreadAnchor;
  const originQuote = displayAnchor?.source_quote;
  const anchorLabel =
    displayAnchor?.source_anchor_kind === "block"
      ? t("threads.paragraphThread")
      : t("threads.messageThread");
  const displayTitle = loadedSession?.title?.trim()
    || threadTitle?.trim()
    || (readOnly ? "Schedule run" : t("threads.newThread"));
  const handleRunComplete = useCallback(async (completedChildSessionId: string) => {
    const reconciliationAgentRevision = agentBindingRevisionRef.current;
    // The mount-time detail can predate auto-title completion. Reuse the
    // parent's authoritative reconciliation result so loadedSession cannot
    // keep winning with that stale value after a live run finishes.
    const reconciled = await onRunComplete?.(completedChildSessionId);
    if (
      reconciled
      && reconciled.id === completedChildSessionId
      && currentChildSessionIdRef.current === completedChildSessionId
      && reconciled.session_type === "thread"
    ) {
      const locallyCommittedAgent = locallyCommittedAgentRef.current;
      // A run-completion reconciliation can begin before an agent PATCH and
      // return its pre-PATCH session afterward. Keep the confirmed binding,
      // just as the detail-load reconciliation does above.
      const reconciledSession = (
        locallyCommittedAgent
        && locallyCommittedAgent.childSessionId === completedChildSessionId
        && locallyCommittedAgent.revision > reconciliationAgentRevision
      )
        ? { ...reconciled, agent_id: locallyCommittedAgent.agentId }
        : reconciled;
      setLoadedSession((current) => (
        current?.id === reconciledSession.id
        && isOlderLastActiveAt(reconciledSession.last_active_at, current.last_active_at)
          ? current
          : reconciledSession
      ));
    }
  }, [onRunComplete]);
  const fallbackBreadcrumbSegments = useMemo(() => {
    const current = { id: childSessionId, title: displayTitle };
    return parentSessionTitle?.trim()
      ? [{ id: parentSessionId, title: parentSessionTitle }, current]
      : [current];
  }, [childSessionId, displayTitle, parentSessionId, parentSessionTitle]);
  const breadcrumbSegments = !readOnly && resolvedBreadcrumb.segments.length > 0
    ? resolvedBreadcrumb.segments
    : fallbackBreadcrumbSegments;
  const currentAgent = allAgents.find((candidate) => candidate.id === selectedAgentId);
  const handleBreadcrumbNavigate = onNavigateSession
    ? (destination: ConversationDestination) => onNavigateSession(destination)
    : readOnly ? (_destination: ConversationDestination) => onClose() : undefined;
  const HeaderIcon = isSubAgentSession ? Bot : MessageSquare;

  const header = (
    <div className={isMobile
      ? "flex shrink-0 items-center justify-between gap-2 border-b px-4 py-3"
      : "flex min-h-[53px] items-center justify-between gap-2 border-b px-3 py-2.5"}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {onBack && <Button variant="ghost" size="icon" onClick={onBack} aria-label={t("workflowRun.back")}>
          <ArrowLeft className="size-4" />
        </Button>}
        <HeaderIcon className="size-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1" data-testid="child-session-title">
          <SessionBreadcrumb
            segments={breadcrumbSegments}
            compact={isMobile}
            onNavigateSession={handleBreadcrumbNavigate}
          />
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {isSubAgentSession && (
          <>
            <AgentInfoTag
              agentId={selectedAgentId}
              agent={currentAgent}
              getToken={getAccessToken}
              compact
            />
            <SubAgentStateBadge
              state={subAgentSnapshot.state}
              availability={subAgentSnapshot.availability}
              staleEmpty={subAgentSnapshot.staleEmpty}
            />
          </>
        )}
        {canManageSession && (
          <AgentDropdown
            agents={allAgents}
            currentAgentId={selectedAgentId}
            loading={false}
            error={null}
            selectionLocked={hasMessages}
            getToken={getAccessToken}
            onSelect={onAgentChange}
            className="min-w-0"
          />
        )}
        {!readOnly && <Button
          variant="ghost"
          size="icon"
          className="size-7 shrink-0"
          onClick={() => onOpenFullSession(childSessionId)}
          aria-label={t("threads.openFullSession")}
          data-testid="child-session-fullscreen"
        >
          <Maximize2 className="size-4" />
        </Button>}
        <Button
          variant="ghost"
          size="icon"
          className="size-7 shrink-0"
          onClick={onClose}
          aria-label={t("viewer.closeViewer")}
          data-testid="child-session-close"
        >
          <X className="size-4" />
        </Button>
      </div>
    </div>
  );

  const terminalEmptyText = subAgentSnapshot.state === "failed"
    || subAgentSnapshot.state === "timed_out"
    ? t("subAgent.failedWithoutOutput")
    : subAgentSnapshot.state === "cancelled"
      ? t("subAgent.cancelledWithoutOutput")
      : subAgentSnapshot.state === "completed"
        ? t("subAgent.completedWithoutOutput")
        : null;
  const subAgentEmptyContent = subAgentSnapshot.staleEmpty ? (
    <div
      className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-muted-foreground"
      data-testid="sub-agent-stale-empty"
      role="status"
    >
      <p className="text-sm font-medium text-foreground">
        {t("subAgent.noOutputMayHaveFailed")}
      </p>
    </div>
  ) : terminalEmptyText ? (
    <div className="flex h-full items-center justify-center p-6 text-center">
      <p className="text-sm font-medium text-muted-foreground">{terminalEmptyText}</p>
    </div>
  ) : (
    <SubAgentStartingState
      displayName={currentAgent?.name ?? selectedAgentId ?? t("subAgent.name")}
      loading={subAgentSnapshot.loading
        || !isTerminalSubAgentState(subAgentSnapshot.state)}
    />
  );
  const panelBody = !loadedSession ? (
    sessionAccessUnavailable ? (
      <SessionUnavailableState />
    ) : (
      <div className="flex min-h-0 flex-1 items-center justify-center" role="status">
        <Spinner className="size-5" />
      </div>
    )
  ) : isSubAgentSession && subAgentSnapshot.availability === "access_denied" ? (
    <SessionUnavailableState />
  ) : isSubAgentSession && subAgentSnapshot.availability === "unavailable" ? (
    <div
      className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 p-6 text-center text-muted-foreground"
      role="alert"
    >
      <p className="text-sm font-medium">{t("subAgent.transcriptUnavailable")}</p>
      <Button type="button" variant="outline" size="sm" onClick={subAgentSnapshot.retry}>
        <RotateCcw aria-hidden="true" className="mr-1.5 size-3.5" />
        {t("common:actions.retry")}
      </Button>
    </div>
  ) : (
    <ChildSessionBody
      childSessionId={childSessionId}
      getAccessToken={getAccessToken}
      agentId={selectedAgentId}
      allAgents={allAgents}
      user={user}
      originQuote={isReadOnly ? undefined : originQuote}
      anchorLabel={anchorLabel}
      onHasMessagesChange={setHasMessages}
      onMessageObservationChange={handleMessageObservationChange}
      onRunComplete={!isReadOnly && onRunComplete ? handleRunComplete : undefined}
      onFileLinkClick={onFileLinkClick}
      onOpenSubAgent={onOpenSubAgent}
      onOpenWorkflowRun={onOpenWorkflowRun}
      onSubAgentReferencesResolved={onSubAgentReferencesResolved}
      canParticipateSession={canParticipateSession}
      canManageSession={canManageSession}
      readOnly={isReadOnly}
      unavailableOnMessageError={readOnly}
      pollIntervalMs={isSubAgentSession
        ? (!isTerminalSubAgentState(subAgentSnapshot.state) && !subAgentSnapshot.staleEmpty
          ? SUB_AGENT_ACTIVE_POLL_INTERVAL_MS
          : IDLE_HISTORY_POLL_INTERVAL_MS)
        : undefined}
      emptyContent={isSubAgentSession ? subAgentEmptyContent : undefined}
      submitShortcut={submitShortcut}
      t={t}
    />
  );

  return (
    <>
      <ChildSessionPanelShell
        mobile={isMobile}
        title={displayTitle}
        header={header}
        onClose={onClose}
        resize={resize}
        resizeLabel={t("viewer.dragToResize")}
        dialogTestId={readOnly ? "schedule-run-session-dialog" : isSubAgentSession ? "sub-agent-session-dialog" : "child-session-dialog"}
        sidebarTestId={readOnly ? "schedule-run-session-sidebar" : isSubAgentSession ? "sub-agent-session-sidebar" : "child-session-sidebar"}
        resizeHandleTestId={isSubAgentSession
          ? "sub-agent-session-resize-handle"
          : "child-session-resize-handle"}
      >
        {panelBody}
      </ChildSessionPanelShell>
    </>
  );
}

function SessionUnavailableState() {
  const { t } = useTranslation("chat");
  return (
    <div
      className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 p-6 text-center text-muted-foreground"
      role="status"
    >
      <p className="text-sm font-medium">{t("subAgent.accountUnavailable")}</p>
      <p className="text-xs">{t("subAgent.transcriptCannotOpen")}</p>
    </div>
  );
}

function SubAgentStartingState({
  displayName,
  loading,
}: {
  displayName: string;
  loading: boolean;
}) {
  const { t } = useTranslation("chat");
  return (
    <div
      className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 p-6 text-center text-muted-foreground"
      data-testid="sub-agent-starting"
      aria-live="polite"
    >
      {loading && <Spinner className="size-5" />}
      <p className="text-sm font-medium text-foreground">{t("subAgent.starting")}</p>
      <p className="text-xs">{t("subAgent.noPersistedResponse", { name: displayName })}</p>
    </div>
  );
}

// ── Shared body content ────────────────────────────────────────────────────

interface ChildSessionBodyProps {
  childSessionId: string;
  getAccessToken: AccessTokenGetter;
  agentId: string | null;
  allAgents: readonly Agent[];
  user: any;
  originQuote?: string;
  anchorLabel: string;
  onHasMessagesChange: (hasMessages: boolean) => void;
  onMessageObservationChange?: (observation: SubAgentMessageObservation) => void;
  onRunComplete?: (childSessionId: string) => void;
  onFileLinkClick?: (url: string, fileName: string) => void;
  onOpenSubAgent?: (reference: SubAgentThreadReference) => void;
  onOpenWorkflowRun?: (taskId: string) => void;
  onSubAgentReferencesResolved?: (
    references: readonly SubAgentThreadReference[],
  ) => void;
  canParticipateSession: boolean;
  canManageSession: boolean;
  readOnly?: boolean;
  unavailableOnMessageError?: boolean;
  pollIntervalMs?: number;
  emptyContent?: ReactNode;
  submitShortcut: "enter" | "ctrl_enter";
  t: (key: string) => string;
}

// Attachment limits mirror the shared composer constants (which in turn
// mirror agno-server UploadService defaults).
const MAX_UPLOAD_FILES = CHAT_MAX_UPLOAD_FILES;
const MAX_UPLOAD_SIZE_MB = CHAT_MAX_UPLOAD_SIZE_MB;
const MAX_UPLOAD_SIZE_BYTES = CHAT_MAX_UPLOAD_SIZE_BYTES;
const IDLE_HISTORY_POLL_INTERVAL_MS = 15_000;
const EMPTY_THREAD_ANCHORS: ThreadAnchorRef[] = [];

function ChildSessionBody(props: ChildSessionBodyProps) {
  // The panel lives outside ChatPage's composer provider, so it owns a
  // separate provider and draft for each child session.
  const initialDraft = useMemo(
    () => getDraft(props.childSessionId).text,
    [props.childSessionId],
  );

  if (props.readOnly) return <ChildSessionChat {...props} />;

  return (
    <PromptInputProvider
      initialInput={initialDraft}
      initialAttachments={getDraftAttemptFiles(props.childSessionId)}
      key={props.childSessionId}
    >
      <ChildSessionChat {...props} />
    </PromptInputProvider>
  );
}

function ChildSessionChat({
  childSessionId,
  getAccessToken,
  agentId,
  allAgents,
  user,
  originQuote,
  anchorLabel,
  onHasMessagesChange,
  onMessageObservationChange,
  onRunComplete,
  onFileLinkClick,
  onOpenSubAgent,
  onOpenWorkflowRun,
  onSubAgentReferencesResolved,
  canParticipateSession,
  canManageSession,
  readOnly = false,
  unavailableOnMessageError = false,
  pollIntervalMs,
  emptyContent,
  submitShortcut,
  t,
}: ChildSessionBodyProps) {
  const { t: tChat } = useTranslation("chat");
  const promptInputController = useOptionalPromptInputController();
  const promptInputControllerRef = useRef(promptInputController);
  promptInputControllerRef.current = promptInputController;
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const email = user?.email;
  const firstName = user?.firstName;
  const lastName = user?.lastName;

  // Match the authenticated fetch wrapper used by the full conversation.
  const fetchWithAuth = useMemo(
    () => async (url: string, init: RequestInit): Promise<Response> => {
      const headers = new Headers(init.headers);
      headers.set("X-User-Email", email ?? "");
      headers.set(
        "X-User-Name",
        firstName ? (firstName + " " + (lastName ?? "")).trim() : "",
      );
      return fetchWithWorkOSAuth(url, { ...init, headers }, getAccessToken);
    },
    [email, firstName, getAccessToken, lastName],
  );

  const handleStatusChange = useCallback(
    (status: ChatStatus, prevStatus: ChatStatus) => {
      const wasInFlight = prevStatus === "submitted" || prevStatus === "streaming";
      if (wasInFlight && (status === "ready" || status === "error")) {
        onRunComplete?.(childSessionId);
      }
    },
    [childSessionId, onRunComplete],
  );
  const {
    hasUserInteracted,
    markUserInteraction,
  } = useConversationReadEngagement(childSessionId);

  const {
    messages,
    sendMessage,
    status,
    isPolling,
    stop,
    continueRun,
    isUploading,
    isTranscribing,
    recoverableFailedRun,
    continueFromFailedRun,
    hasMoreMessages,
    totalMessages,
    isLoadingMore,
    loadMoreMessages,
    olderPageError,
    newerPageError,
    isPrepending,
    prependRevision,
    hasLoadedMessages,
    messageLoadError,
    handleRetryMessageLoad,
    loadNavigationTarget,
    hasNewerWindowActivity,
    jumpToLatest,
    navigationCoverage,
    navigationContentMessageId,
    navigationContentError,
    continueNavigationContent,
    cancelNavigationContent,
    isLoadingNewerNavigation,
    loadNewerNavigationMessages,
  } = useSessionMessageFeed({
    readOnly,
    sessionId: childSessionId,
    agentId,
    fetchWithAuth,
    onStatusChange: handleStatusChange,
    onHasMessagesChange,
    pollIntervalMs,
    hasUserInteracted,
  });

  const isMobileComposer = useIsMobile();
  const enterToSendHint = useEnterToSendHint({ submitShortcut, isMobile: isMobileComposer, sessionId: childSessionId });
  const [focusTick, setFocusTick] = useState(0);
  useEffect(() => setFocusTick((tick) => tick + 1), [childSessionId]);
  const [composerDraft, setComposerDraft] = useState(Boolean(promptInputController?.textInput.value.trim()));
  const [localActionMessages, setLocalActionMessages] = useState<AgnoChatMessage[]>([]);
  useEffect(() => { setLocalActionMessages([]); setComposerDraft(Boolean(promptInputController?.textInput.value.trim())); }, [childSessionId]);
  const composerActions = allAgents.find((agent) => agent.id === agentId)?.actions ?? [];
  const transcriptMessages = useMemo(() => {
    const canonicalTasks = new Set(messages.map((message) => message.metadata?.task_id).filter(Boolean));
    return [...messages, ...localActionMessages.filter((message) => !canonicalTasks.has(message.metadata?.task_id))];
  }, [messages, localActionMessages]);
  const transcriptActionRuns = useMemo(() => transcriptMessages.flatMap((message) => {
    const metadata = message.metadata;
    return metadata?.source === "composer_action" && typeof metadata.task_id === "string" && typeof metadata.action_id === "string"
      ? [{ taskId: metadata.task_id, actionId: metadata.action_id }] : [];
  }), [transcriptMessages]);
  const handleComposerActionQueued = useCallback((action: { id: string; label: string }, taskId: string) => {
    const text = tChat("composerActions.started", { label: action.label, run: taskId.slice(0, 8) });
    setLocalActionMessages((previous) => [...previous, {
      id: `composer-action-${taskId}`, role: "user", parts: [{ type: "text", text }], content: text,
      metadata: { source: "composer_action", action_id: action.id, action_label: action.label, task_id: taskId },
    }]);
  }, [tChat]);
  const {
    promptQueue, submitError, editingPromptId, setEditingPromptId, editingPromptText, setEditingPromptText,
    editQueuedPrompt, deleteQueuedPrompt, queueIfBusy, submitDisabled,
    promptResendSupported, promptResendError, promptDeleteError, resendingPromptId, resendQueuedPrompt,
  } = usePromptQueue({ sessionId: childSessionId, agentId, status, messages, fetchWithAuth });
  const isStreaming = status === "submitted" || status === "streaming";

  useEffect(() => {
    onMessageObservationChange?.({
      count: messages.length,
      state: subAgentMessageStateFromRows(messages),
    });
  }, [messages, onMessageObservationChange]);

  // Persist plain-text drafts per thread and retain failed-send file payloads
  // in the companion draft-attempt store so a remounted thread can retry.
  const initialDraft = useMemo(() => getDraft(childSessionId).text, [childSessionId]);
  const agentSkills = useAgentSkills(agentId, childSessionId, getAccessToken);
  const selectedSkillsRef = useRef(agentSkills.selected);
  selectedSkillsRef.current = agentSkills.selected;
  const draftSavedRef = useRef(initialDraft);
  const draftAttemptRef = useRef<DraftAttempt | null>(getDraftAttempt(childSessionId));
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingDraftRef = useRef<{ sessionId: string; text: string } | null>(null);

  const flushDraft = useCallback(() => {
    if (draftTimerRef.current) {
      clearTimeout(draftTimerRef.current);
      draftTimerRef.current = null;
    }
    const pending = pendingDraftRef.current;
    if (pending) {
      setDraft(pending.sessionId, pending.text, selectedSkillsRef.current);
      pendingDraftRef.current = null;
    }
  }, []);

  const scheduleDraftSave = useCallback(
    (sessionId: string, text: string) => {
      pendingDraftRef.current = { sessionId, text };
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
      draftTimerRef.current = setTimeout(flushDraft, 400);
    },
    [flushDraft],
  );

  const cancelPendingDraft = useCallback(() => {
    if (draftTimerRef.current) {
      clearTimeout(draftTimerRef.current);
      draftTimerRef.current = null;
    }
    pendingDraftRef.current = null;
  }, []);

  // A switch between sidebar and full-session view can unmount this
  // composer before the debounce runs, so flush immediately on cleanup.
  useEffect(() => flushDraft, [childSessionId, flushDraft]);

  useEffect(() => {
    const handleDraftChange = () => {
      const attempt = draftAttemptRef.current;
      if (!attempt || getDraftAttempt(childSessionId)) return;

      if (mountedRef.current && promptInputController) {
        const currentFiles = promptInputController.attachments.files;
        const filesMatch = currentFiles.length === attempt.files.length
          && currentFiles.every((file, index) => {
            const attemptedFile = attempt.files[index];
            return file.rawFile === attemptedFile.rawFile
              && file.filename === attemptedFile.filename
              && file.mediaType === attemptedFile.mediaType;
          });
        if (
          promptInputController.textInput.value === attempt.text
          && filesMatch
        ) {
          promptInputController.textInput.clear();
          promptInputController.attachments.clear();
        }
      }
      draftAttemptRef.current = null;
    };

    window.addEventListener(DRAFTS_CHANGED_EVENT, handleDraftChange);
    return () => window.removeEventListener(DRAFTS_CHANGED_EVENT, handleDraftChange);
  }, [childSessionId, promptInputController]);

  const restoreDraftAttempt = useCallback((attempt: DraftAttempt) => {
    draftAttemptRef.current = attempt;
    const controller = promptInputControllerRef.current;
    if (mountedRef.current && controller) {
      if (controller.textInput.value.trim() === "") {
        controller.textInput.setInput(attempt.text);
      }
      controller.attachments.restore(attempt.files);
    }
  }, []);

  const settleDraftAttempt = useCallback(
    (
      attempt: DraftAttempt,
      result: Awaited<ReturnType<typeof sendMessage>>,
      source: string,
    ) => {
      if (result.outcome === "sent") {
        completeDraftAttempt(childSessionId, attempt.id);
        agentSkills.clearIfMatches(attempt.selectedSkills);
        return;
      }

      restoreDraftAttempt(attempt);
      if (selectedSkillsRef.current.length === 0) agentSkills.restore(attempt.selectedSkills);
      console.error(`[child-session-panel] ${source} was not delivered; restored the draft`, result.error);
    },
    [agentSkills.clearIfMatches, agentSkills.restore, childSessionId, restoreDraftAttempt, sendMessage],
  );

  const handleSubmit = useCallback(
    ({ text, files }: { text: string; files: any[] }) => {
      cancelPendingDraft();
      const attempt = beginDraftAttempt(childSessionId, text, files, selectedSkillsRef.current);
      const submittedText = prependSelectedSkills(attempt.selectedSkills, text);
      draftAttemptRef.current = attempt;
      draftSavedRef.current = text;
      setComposerDraft(false);
      enterToSendHint.hide();
      enterToSendHint.notifySubmit(text);
      const delivery = isStreaming ? queueIfBusy(submittedText, files).then((queued) => queued ? { outcome: "sent" as const } : sendMessage({
        text: submittedText,
        files: files.length > 0 ? files : undefined,
      })) : sendMessage({ text: submittedText, files: files.length > 0 ? files : undefined });
      void delivery
        .then((result) => settleDraftAttempt(attempt, result, "Message send"))
        .catch((error) => {
          restoreDraftAttempt(attempt);
          if (selectedSkillsRef.current.length === 0) agentSkills.restore(attempt.selectedSkills);
          console.error("[child-session-panel] sendMessage error; restored the draft:", error);
        });
    },
    [
      agentSkills.restore,
      cancelPendingDraft,
      childSessionId,
      restoreDraftAttempt,
      sendMessage,
      settleDraftAttempt,
      queueIfBusy,
      enterToSendHint,
      isStreaming,
    ],
  );

  const handleSubmitVoiceNote = useCallback(
    (
      text: string,
      file: File,
      existingFiles: FileUIPart[] = [],
    ) => {
      cancelPendingDraft();
      const voiceFile: FileUIPart & { rawFile?: File } = {
        type: "file" as const,
        filename: file.name,
        mediaType: file.type,
        url: URL.createObjectURL(file),
        rawFile: file,
      };
      const files = [...existingFiles, voiceFile];
      const attempt = beginDraftAttempt(childSessionId, text, files);
      draftAttemptRef.current = attempt;
      draftSavedRef.current = text;
      void sendMessage({
        text,
        files: files.length > 0 ? files : undefined,
      })
        .then((result) => settleDraftAttempt(attempt, result, "Voice-note send"))
        .catch((error) => {
          restoreDraftAttempt(attempt);
          console.error("[child-session-panel] sendMessage (voice note) error; restored the draft:", error);
        });
    },
    [
      cancelPendingDraft,
      childSessionId,
      restoreDraftAttempt,
      sendMessage,
      settleDraftAttempt,
    ],
  );

  const voiceNoteRef = useRef<VoiceNoteButtonHandle>(null);
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const handleVoiceSubmit = useCallback(() => {
    voiceNoteRef.current?.submitVoiceNote();
  }, []);

  const canInteract = canParticipateSession && !readOnly;

  if (unavailableOnMessageError && messageLoadError) return <SessionUnavailableState />;

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <ConversationMessageList
        sessionId={childSessionId}
        messages={transcriptMessages}
        status={status}
        isPolling={isPolling}
        agentId={agentId}
        fetchWithAuth={fetchWithAuth}
        continueRun={continueRun}
        canManageSession={!readOnly && canManageSession}
        readOnly={readOnly}
        allAgents={allAgents}
        onOpenSubAgent={onOpenSubAgent}
        onOpenWorkflowRun={onOpenWorkflowRun}
        onSubAgentReferencesResolved={onSubAgentReferencesResolved}
        onFileLinkClick={onFileLinkClick}
        threadAnchors={EMPTY_THREAD_ANCHORS}
        threadCreation={{ enabled: false }}
        hasMore={hasMoreMessages}
        totalMessages={totalMessages}
        isLoadingMore={isLoadingMore}
        olderPageError={olderPageError}
        newerPageError={newerPageError}
        isPrepending={isPrepending}
        prependRevision={prependRevision}
        loadOlder={loadMoreMessages}
        hasLoaded={hasLoadedMessages}
        error={messageLoadError}
        retry={handleRetryMessageLoad}
        loadNavigationTarget={loadNavigationTarget}
        hasNewerWindowActivity={hasNewerWindowActivity}
        jumpToLatest={jumpToLatest}
        navigationCoverage={navigationCoverage}
        navigationContentMessageId={navigationContentMessageId}
        navigationContentError={navigationContentError}
        continueNavigationContent={continueNavigationContent}
        cancelNavigationContent={cancelNavigationContent}
        isLoadingNewerNavigation={isLoadingNewerNavigation}
        loadNewerNavigationMessages={loadNewerNavigationMessages}
        onUserInteraction={markUserInteraction}
        emptyContent={emptyContent}
        leadingContent={originQuote && (
          <div
            className="max-h-40 overflow-y-auto border-b bg-muted/40 px-4 py-3"
            data-testid="thread-origin-banner"
          >
            <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <ArrowLeft className="size-3" />
              <span>{anchorLabel}</span>
            </div>
            <blockquote className="border-l-2 border-primary/40 pl-3 text-muted-foreground">
              <MarkdownViewer
                content={originQuote}
                editable={false}
                className="text-sm"
              />
            </blockquote>
          </div>
        )}
      />

      {canInteract && (
        <InterruptedRunBanner
          recoverableFailedRun={recoverableFailedRun}
          onContinue={continueFromFailedRun}
        />
      )}

      {canInteract && (
        <ChatPromptComposer
          inputKey={childSessionId}
          autoFocusKey={`${childSessionId}:${focusTick ?? 0}`}
          textareaName="message"
          placeholder={t("input.placeholder")}
          onSubmit={handleSubmit}
          onVoiceNoteSubmit={handleSubmitVoiceNote}
          voiceNoteRef={voiceNoteRef}
          onRecordingChange={setIsRecordingVoice}
          voiceDictationShortcutTarget="thread-chat"
          onVoiceDictationShortcut={() => dispatchVoiceDictationShortcut("thread-chat")}
          onInputChange={(value) => { setComposerDraft(Boolean(value.trim())); scheduleDraftSave(childSessionId, value); }}
          onKeyDown={(event) => { markUserInteraction(event.nativeEvent); enterToSendHint.onKeyDown(event); }}
          submitShortcut={submitShortcut}
          status={status}
          isUploading={isUploading}
          isTranscribing={isTranscribing}
          submitDisabled={submitDisabled && !isRecordingVoice}
          className="flex shrink-0 flex-col gap-2 border-t bg-background p-3"
          multiple
          maxFiles={MAX_UPLOAD_FILES}
          maxFileSize={MAX_UPLOAD_SIZE_BYTES}
          onError={(err) => {
            if (err.code === "max_files") console.warn(`[ui] Attachment rejected: max ${MAX_UPLOAD_FILES} files`);
            else if (err.code === "max_file_size") console.warn(`[ui] Attachment rejected: max ${MAX_UPLOAD_SIZE_MB} MB per file`);
            else if (err.code === "accept") console.warn("[ui] Attachment rejected: file type not accepted");
          }}
          promptQueue={<PromptQueueList items={promptQueue} editingId={editingPromptId} setEditingId={setEditingPromptId} editingText={editingPromptText} setEditingText={setEditingPromptText} onEdit={editQueuedPrompt} onDelete={deleteQueuedPrompt} submitError={submitError} resendError={promptResendError} deleteError={promptDeleteError} resendSupported={promptResendSupported} resendingId={resendingPromptId} onResend={resendQueuedPrompt} />}
          enterToSendHint={<EnterToSendHint open={enterToSendHint.open} onDismiss={enterToSendHint.dismiss} onOpenPreferences={() => { window.dispatchEvent(new CustomEvent(OPEN_PREFERENCES_EVENT)); enterToSendHint.hide(); }} />}
          followUpSuggestions={!readOnly ? <FollowUpSuggestions sessionId={childSessionId} lastAssistantMessageId={lastCompletedAssistantMessageId(messages)} completedSuccessfully={status === "ready"} writable={canParticipateSession} status={status} getToken={getAccessToken} hasDraft={composerDraft || Boolean(promptInputController?.textInput.value.trim())} onSubmit={handleSubmit} /> : undefined}
          selectedSkillBadges={<SelectedSkillBadges skills={agentSkills.selected} onRemove={(name) => { flushDraft(); agentSkills.remove(name); }} />}
          quickActions={({ stacked }) => agentId ? <ComposerQuickActions stacked={stacked} key={`${childSessionId}:${agentId}`} actions={composerActions} agentId={agentId} sessionId={childSessionId} getToken={getAccessToken} hasDraft={composerDraft || Boolean(promptInputController?.textInput.value.trim())} narrow disabled={isStreaming || isUploading || isTranscribing} onQueued={handleComposerActionQueued} transcriptRuns={transcriptActionRuns} skillsStatus={agentSkills.status} skills={agentSkills.skills} selectedSkills={agentSkills.selected} skillsLimitReached={agentSkills.limitReached} onToggleSkill={(name) => { flushDraft(); agentSkills.toggle(name); }} /> : undefined}
          uploadProgress={<div className="flex items-center gap-2 px-1 text-xs text-muted-foreground"><Spinner className="size-3.5" /><span>{t("common:status.uploadingFiles")}</span></div>}
          transcriptionProgress={<div className="flex items-center gap-2 px-1 text-xs text-muted-foreground"><Spinner className="size-3.5" /><span>{t("common:status.transcribingVoiceNote")}</span></div>}
          stopControl={isStreaming && canManageSession ? <InputGroupButton type="button" variant="default" size="icon-sm" aria-label={t("input.stop")} onClick={stop}><SquareIcon className="size-4" /></InputGroupButton> : undefined}
        />
      )}
    </div>
  );
}
